// src/components/rpa/ClientsPanel.js
// Client and Findings/Issues dashboard module
// ⭐ UPDATED (Aug 2026): Remediation tab now renders RemediationPanel, which
// provides the working UI for the two-party remediation flow (corrective
// actions, management assertion, independent auditor verification, gated
// closure). Everything else in this file is unchanged.
//
// Self-contained so RpaDashboard.js needs only two inserted lines (an import
// and a render) — that file is ~6,250 lines and is not safe to hand-regenerate.
// Same props as before (onOpenProject, onOpenDocument): drop-in replacement,
// no RpaDashboard changes required.
//
// Three views, all inside this one component:
//   list    — portfolio analytics band + client risk cards
//   detail  — one client: severity donut, type/status charts, projects,
//             documents, remediation tracker, interactive issues table
//   dupes   — near-duplicate name groups with a merge action
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import RemediationPanel from './RemediationPanel';
const API_URL = process.env.REACT_APP_API || 'http://localhost:8000';
const OPEN_STATUSES = ['open', 'in_remediation', 'pending_review', 'escalated'];
const SEVERITY_ORDER = ['critical', 'high', 'moderate', 'low'];
const SEVERITY_COLORS = {
  critical: { bg: '#fef2f2', border: '#ef4444', text: '#991b1b', solid: '#ef4444' },
  high: { bg: '#fff7ed', border: '#f97316', text: '#9a3412', solid: '#f97316' },
  moderate: { bg: '#fefce8', border: '#eab308', text: '#854d0e', solid: '#eab308' },
  low: { bg: '#f0f9ff', border: '#38bdf8', text: '#075985', solid: '#38bdf8' }
};
const STATUS_COLORS = {
  open: { bg: '#fef2f2', text: '#991b1b', solid: '#ef4444' },
  in_remediation: { bg: '#fff7ed', text: '#9a3412', solid: '#f97316' },
  pending_review: { bg: '#eff6ff', text: '#1e40af', solid: '#3b82f6' },
  escalated: { bg: '#faf5ff', text: '#7c3aed', solid: '#8b5cf6' },
  closed: { bg: '#f0fdf4', text: '#065f46', solid: '#22c55e' },
  voided: { bg: '#f3f4f6', text: '#4b5563', solid: '#9ca3af' }
};
// Palette for the "open issues by client" portfolio donut
const CLIENT_PALETTE = ['#6366f1', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#0ea5e9', '#94a3b8'];
const fmtDate = (d) => {
  if (!d) return '-';
  try {
    return new Date(d).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  } catch {
    return '-';
  }
};
const fmtMoney = (n) => {
  if (typeof n !== 'number' || Number.isNaN(n)) return null;
  return n.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
};
const titleCase = (s) => String(s || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
// ─────────────────────────────────────────────────────────────── chart bits
// Pure-SVG donut. segments: [{ key, label, value, color }]. Clicking a segment
// calls onSegmentClick(key). activeKey dims the others.
const Donut = ({ segments, size = 150, stroke = 24, centerTop, centerBottom, onSegmentClick, activeKey }) => {
  const total = segments.reduce((s, x) => s + (x.value || 0), 0);
  const r = (size - stroke) / 2;
  const C = 2 * Math.PI * r;
  let acc = 0;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ flexShrink: 0 }}>
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke="#f3f4f6"
        strokeWidth={stroke}
      />
      {total > 0 &&
        segments
          .filter((s) => s.value > 0)
          .map((s) => {
            const frac = s.value / total;
            const dash = frac * C;
            const offset = -acc * C;
            acc += frac;
            const dimmed = activeKey && activeKey !== s.key;
            return (
              <circle
                key={s.key}
                cx={size / 2}
                cy={size / 2}
                r={r}
                fill="none"
                stroke={s.color}
                strokeWidth={activeKey === s.key ? stroke + 4 : stroke}
                strokeDasharray={`${dash} ${C - dash}`}
                strokeDashoffset={offset}
                transform={`rotate(-90 ${size / 2} ${size / 2})`}
                style={{
                  cursor: onSegmentClick ? 'pointer' : 'default',
                  opacity: dimmed ? 0.25 : 1,
                  transition: 'opacity .15s ease, stroke-width .15s ease'
                }}
                onClick={onSegmentClick ? () => onSegmentClick(s.key) : undefined}
              >
                <title>{`${s.label}: ${s.value}`}</title>
              </circle>
            );
          })}
      <text
        x="50%"
        y="47%"
        textAnchor="middle"
        style={{ fontSize: 24, fontWeight: 800, fill: '#111827' }}
      >
        {centerTop}
      </text>
      <text
        x="50%"
        y="60%"
        textAnchor="middle"
        style={{ fontSize: 10, fontWeight: 700, fill: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5 }}
      >
        {centerBottom}
      </text>
    </svg>
  );
};
// Horizontal CSS bar list. rows: [{ key, label, value, color, sub }]
const BarList = ({ rows, onRowClick, maxRows = 8 }) => {
  const max = Math.max(1, ...rows.map((r) => r.value));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%' }}>
      {rows.slice(0, maxRows).map((r) => (
        <div
          key={r.key}
          onClick={onRowClick ? () => onRowClick(r.key) : undefined}
          style={{ cursor: onRowClick ? 'pointer' : 'default' }}
          className="cp-barrow"
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 3 }}>
            <span style={{ fontWeight: 600, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '75%' }}>
              {r.label}
            </span>
            <span style={{ fontWeight: 700, color: '#111827' }}>
              {r.value}{r.sub ? <span style={{ color: '#9ca3af', fontWeight: 500 }}> {r.sub}</span> : null}
            </span>
          </div>
          <div style={{ height: 8, background: '#f3f4f6', borderRadius: 999, overflow: 'hidden' }}>
            <div
              className="cp-bar-fill"
              style={{
                height: '100%',
                width: `${Math.round((r.value / max) * 100)}%`,
                background: r.color,
                borderRadius: 999
              }}
            />
          </div>
        </div>
      ))}
    </div>
  );
};
// Thin stacked distribution strip (e.g. status mix)
const StackStrip = ({ segments, height = 10 }) => {
  const total = segments.reduce((s, x) => s + (x.value || 0), 0);
  if (!total) return null;
  return (
    <div style={{ display: 'flex', width: '100%', height, borderRadius: 999, overflow: 'hidden', background: '#f3f4f6' }}>
      {segments
        .filter((s) => s.value > 0)
        .map((s) => (
          <div
            key={s.key}
            title={`${s.label}: ${s.value}`}
            style={{ width: `${(s.value / total) * 100}%`, background: s.color }}
          />
        ))}
    </div>
  );
};
const ClientsPanel = ({ onOpenProject, onOpenDocument }) => {
  const [view, setView] = useState('list'); // 'list' | 'detail' | 'dupes'
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailTab, setDetailTab] = useState('overview');
  const [issueStatusFilter, setIssueStatusFilter] = useState('all');
  const [issueSeverityFilter, setIssueSeverityFilter] = useState('all');
  const [issueTypeFilter, setIssueTypeFilter] = useState('all');
  const [expandedIssueId, setExpandedIssueId] = useState(null);
  // Full issue docs (with description / managementResponse / remediationPlan)
  // from the client-level rollup endpoint. detail.issues is a slim projection.
  const [fullIssues, setFullIssues] = useState(null);
  const [fullIssuesLoading, setFullIssuesLoading] = useState(false);
  // ⭐ summary.remediation from the rollup endpoint — server-computed stage
  // coverage and overdue metrics, passed straight to RemediationPanel.
  const [remediationSummary, setRemediationSummary] = useState(null);
  const [dupes, setDupes] = useState([]);
  const [dupesLoading, setDupesLoading] = useState(false);
  const [merging, setMerging] = useState(null);
  const [showNewClient, setShowNewClient] = useState(false);
  const [newClient, setNewClient] = useState({ name: '', contactName: '', contactEmail: '', contactPhone: '' });
  const [savingClient, setSavingClient] = useState(false);
  const [editing, setEditing] = useState(null);
  // ---------------------------------------------------------------- fetching
  const fetchClients = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await axios.get(`${API_URL}/admin/rpa/clients`, {
        params: { status: statusFilter },
        withCredentials: true
      });
      setClients(res.data?.clients || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load clients');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);
  useEffect(() => {
    fetchClients();
  }, [fetchClients]);
  const fetchDetail = useCallback(async (clientId) => {
    try {
      setDetailLoading(true);
      setError('');
      const res = await axios.get(`${API_URL}/admin/rpa/clients/${clientId}`, {
        withCredentials: true
      });
      setDetail(res.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load client');
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  }, []);
  const fetchFullIssues = useCallback(async (clientId) => {
    try {
      setFullIssuesLoading(true);
      const res = await axios.get(`${API_URL}/admin/rpa/clients/${clientId}/issues`, {
        params: { status: 'all' },
        withCredentials: true
      });
      setFullIssues(res.data?.issues || []);
      setRemediationSummary(res.data?.summary?.remediation || null);
    } catch {
      // Fall back silently to the slim detail.issues projection
      setFullIssues(null);
      setRemediationSummary(null);
    } finally {
      setFullIssuesLoading(false);
    }
  }, []);
  const openClient = (clientId) => {
    setSelectedId(clientId);
    setView('detail');
    setDetailTab('overview');
    setIssueStatusFilter('all');
    setIssueSeverityFilter('all');
    setIssueTypeFilter('all');
    setExpandedIssueId(null);
    setFullIssues(null);
    setRemediationSummary(null);
    fetchDetail(clientId);
    fetchFullIssues(clientId);
  };
  // ⭐ Called by RemediationPanel after any successful mutation.
  const refreshAfterRemediation = useCallback(async () => {
    if (!selectedId) return;
    await Promise.all([fetchDetail(selectedId), fetchFullIssues(selectedId)]);
    fetchClients();
  }, [selectedId, fetchDetail, fetchFullIssues, fetchClients]);
  const fetchDupes = async () => {
    try {
      setDupesLoading(true);
      setError('');
      const res = await axios.get(`${API_URL}/admin/rpa/clients/duplicates`, {
        withCredentials: true
      });
      setDupes(res.data?.groups || []);
      setView('dupes');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to scan for duplicates');
    } finally {
      setDupesLoading(false);
    }
  };
  // ---------------------------------------------------------------- mutations
  const createClient = async () => {
    const name = newClient.name.trim();
    if (!name) {
      setError('Client name is required');
      return;
    }
    try {
      setSavingClient(true);
      setError('');
      await axios.post(`${API_URL}/admin/rpa/clients`, { ...newClient, name }, { withCredentials: true });
      setNewClient({ name: '', contactName: '', contactEmail: '', contactPhone: '' });
      setShowNewClient(false);
      await fetchClients();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create client');
    } finally {
      setSavingClient(false);
    }
  };
  const saveEdit = async () => {
    if (!editing) return;
    try {
      setSavingClient(true);
      setError('');
      await axios.patch(
        `${API_URL}/admin/rpa/clients/${editing._id}`,
        {
          name: editing.name,
          contactName: editing.contactName,
          contactEmail: editing.contactEmail,
          contactPhone: editing.contactPhone,
          adminNotes: editing.adminNotes
        },
        { withCredentials: true }
      );
      setEditing(null);
      await fetchClients();
      if (view === 'detail' && selectedId) await fetchDetail(selectedId);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update client');
    } finally {
      setSavingClient(false);
    }
  };
  const toggleArchive = async (client, archived) => {
    try {
      setError('');
      await axios.patch(
        `${API_URL}/admin/rpa/clients/${client._id}/archive`,
        { archived },
        { withCredentials: true }
      );
      await fetchClients();
      if (view === 'detail' && selectedId) await fetchDetail(selectedId);
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to change client status';
      if (err.response?.status === 409 && window.confirm(`${msg}\n\nArchive anyway?`)) {
        try {
          await axios.patch(
            `${API_URL}/admin/rpa/clients/${client._id}/archive`,
            { archived, force: true },
            { withCredentials: true }
          );
          await fetchClients();
          if (view === 'detail' && selectedId) await fetchDetail(selectedId);
          return;
        } catch (e2) {
          setError(e2.response?.data?.message || 'Failed to archive');
          return;
        }
      }
      setError(msg);
    }
  };
  const mergeInto = async (survivorId, sourceId, sourceName, survivorName) => {
    if (!window.confirm(`Merge "${sourceName}" into "${survivorName}"?\n\nAll its projects and documents move to the surviving record. This cannot be undone from the UI.`)) {
      return;
    }
    try {
      setMerging(sourceId);
      setError('');
      await axios.post(
        `${API_URL}/admin/rpa/clients/${survivorId}/merge`,
        { sourceClientId: sourceId },
        { withCredentials: true }
      );
      await fetchDupes();
      await fetchClients();
    } catch (err) {
      const data = err.response?.data;
      if (err.response?.status === 409 && data?.collisions?.length) {
        const names = data.collisions.map((c) => c.name).join(', ');
        if (window.confirm(`${data.message}\n\nColliding projects: ${names}\n\nRename them automatically and continue?`)) {
          try {
            await axios.post(
              `${API_URL}/admin/rpa/clients/${survivorId}/merge`,
              { sourceClientId: sourceId, renameCollisions: true },
              { withCredentials: true }
            );
            await fetchDupes();
            await fetchClients();
            return;
          } catch (e2) {
            setError(e2.response?.data?.message || 'Merge failed');
            return;
          }
        }
        return;
      }
      setError(data?.message || 'Merge failed');
    } finally {
      setMerging(null);
    }
  };
  // ---------------------------------------------------------------- rendering
  const filtered = clients.filter((c) =>
    !search.trim() ? true : c.name.toLowerCase().includes(search.trim().toLowerCase())
  );
  const totals = clients.reduce(
    (acc, c) => ({
      projects: acc.projects + (c.projectCount || 0),
      docs: acc.docs + (c.docCount || 0),
      issues: acc.issues + (c.issueCount || 0),
      open: acc.open + (c.openIssueCount || 0),
      critical: acc.critical + (c.criticalIssueCount || 0)
    }),
    { projects: 0, docs: 0, issues: 0, open: 0, critical: 0 }
  );
  const renderHeader = () => (
    <div style={headerRow}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        {view !== 'list' && (
          <button onClick={() => { setView('list'); setDetail(null); setFullIssues(null); setRemediationSummary(null); }} style={backButton}>
            ← All clients
          </button>
        )}
        <h2 style={h2}>
          {view === 'detail' ? detail?.client?.name || 'Client' : view === 'dupes' ? 'Possible duplicates' : 'Clients & Issues'}
        </h2>
      </div>
      {view === 'list' && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            type="text"
            placeholder="Search clients"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ ...input, width: 200 }}
          />
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={input}>
            <option value="active">Active</option>
            <option value="archived">Archived</option>
            <option value="all">All</option>
          </select>
          <button onClick={fetchDupes} style={secondaryButton} disabled={dupesLoading}>
            {dupesLoading ? 'Scanning…' : 'Find duplicates'}
          </button>
          <button onClick={() => setShowNewClient(!showNewClient)} style={showNewClient ? cancelButton : primaryButton}>
            {showNewClient ? 'Cancel' : '+ New Client'}
          </button>
        </div>
      )}
    </div>
  );
  const renderNewClientForm = () => (
    <div style={formCard}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10 }}>
        <input
          type="text"
          placeholder="Client name *"
          value={newClient.name}
          onChange={(e) => setNewClient({ ...newClient, name: e.target.value })}
          style={input}
        />
        <input
          type="text"
          placeholder="Primary contact"
          value={newClient.contactName}
          onChange={(e) => setNewClient({ ...newClient, contactName: e.target.value })}
          style={input}
        />
        <input
          type="email"
          placeholder="Contact email"
          value={newClient.contactEmail}
          onChange={(e) => setNewClient({ ...newClient, contactEmail: e.target.value })}
          style={input}
        />
        <input
          type="text"
          placeholder="Contact phone"
          value={newClient.contactPhone}
          onChange={(e) => setNewClient({ ...newClient, contactPhone: e.target.value })}
          style={input}
        />
      </div>
      <button onClick={createClient} disabled={savingClient} style={{ ...primaryButton, marginTop: 12 }}>
        {savingClient ? 'Saving…' : 'Create client'}
      </button>
    </div>
  );
  const renderEditForm = () => (
    <div style={{ ...formCard, borderColor: '#4f46e5' }}>
      <div style={{ fontWeight: 700, marginBottom: 10 }}>Edit client</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10 }}>
        <input
          type="text"
          value={editing.name || ''}
          onChange={(e) => setEditing({ ...editing, name: e.target.value })}
          placeholder="Client name *"
          style={input}
        />
        <input
          type="text"
          value={editing.contactName || ''}
          onChange={(e) => setEditing({ ...editing, contactName: e.target.value })}
          placeholder="Primary contact"
          style={input}
        />
        <input
          type="email"
          value={editing.contactEmail || ''}
          onChange={(e) => setEditing({ ...editing, contactEmail: e.target.value })}
          placeholder="Contact email"
          style={input}
        />
        <input
          type="text"
          value={editing.contactPhone || ''}
          onChange={(e) => setEditing({ ...editing, contactPhone: e.target.value })}
          placeholder="Contact phone"
          style={input}
        />
      </div>
      <textarea
        value={editing.adminNotes || ''}
        onChange={(e) => setEditing({ ...editing, adminNotes: e.target.value })}
        placeholder="Internal notes"
        style={{ ...input, width: '100%', minHeight: 60, marginTop: 10, resize: 'vertical' }}
      />
      <div style={{ fontSize: 12, color: '#6b7280', marginTop: 8 }}>
        Renaming cascades to every project and document under this client.
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <button onClick={saveEdit} disabled={savingClient} style={primaryButton}>
          {savingClient ? 'Saving…' : 'Save changes'}
        </button>
        <button onClick={() => setEditing(null)} style={secondaryButton}>Cancel</button>
      </div>
    </div>
  );
  // ─────────────────────────────────────────────────────────── LIST VIEW
  const renderPortfolioBand = () => {
    if (!clients.length) return null;
    const byClientOpen = clients
      .filter((c) => (c.openIssueCount || 0) > 0)
      .sort((a, b) => (b.openIssueCount || 0) - (a.openIssueCount || 0));
    const donutSegs = byClientOpen.slice(0, 6).map((c, idx) => ({
      key: String(c._id),
      label: c.name,
      value: c.openIssueCount || 0,
      color: CLIENT_PALETTE[idx % CLIENT_PALETTE.length]
    }));
    const restOpen = byClientOpen.slice(6).reduce((s, c) => s + (c.openIssueCount || 0), 0);
    if (restOpen > 0) {
      donutSegs.push({ key: '__other', label: 'Other clients', value: restOpen, color: CLIENT_PALETTE[6] });
    }
    const barRows = byClientOpen.slice(0, 6).map((c, idx) => ({
      key: String(c._id),
      label: c.name,
      value: c.openIssueCount || 0,
      sub: c.criticalIssueCount ? `(${c.criticalIssueCount} critical)` : '',
      color: c.criticalIssueCount ? SEVERITY_COLORS.critical.solid : CLIENT_PALETTE[idx % CLIENT_PALETTE.length]
    }));
    return (
      <div style={analyticsBand}>
        <div style={kpiRow}>
          <div style={{ ...kpiCard, borderTopColor: '#6366f1' }}>
            <div style={kpiNum}>{clients.length}</div>
            <div style={kpiLabel}>Clients</div>
          </div>
          <div style={{ ...kpiCard, borderTopColor: '#8b5cf6' }}>
            <div style={kpiNum}>{totals.projects}</div>
            <div style={kpiLabel}>Projects</div>
          </div>
          <div style={{ ...kpiCard, borderTopColor: '#0ea5e9' }}>
            <div style={kpiNum}>{totals.docs}</div>
            <div style={kpiLabel}>Documents</div>
          </div>
          <div style={{ ...kpiCard, borderTopColor: totals.open ? '#ef4444' : '#22c55e' }}>
            <div style={{ ...kpiNum, color: totals.open ? '#b91c1c' : '#065f46' }}>{totals.open}</div>
            <div style={kpiLabel}>Open issues</div>
          </div>
          <div style={{ ...kpiCard, borderTopColor: totals.critical ? '#ef4444' : '#e5e7eb' }}>
            <div style={{ ...kpiNum, color: totals.critical ? '#b91c1c' : '#111827' }}>{totals.critical}</div>
            <div style={kpiLabel}>Critical open</div>
          </div>
        </div>
        {totals.open > 0 && (
          <div style={chartsRow}>
            <div style={chartCard}>
              <div style={chartTitle}>Open issues by client</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                <Donut
                  segments={donutSegs}
                  centerTop={totals.open}
                  centerBottom="Open"
                  onSegmentClick={(key) => { if (key !== '__other') openClient(key); }}
                />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 5, minWidth: 140 }}>
                  {donutSegs.map((s) => (
                    <div
                      key={s.key}
                      onClick={() => { if (s.key !== '__other') openClient(s.key); }}
                      style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12, cursor: s.key === '__other' ? 'default' : 'pointer' }}
                    >
                      <span style={{ width: 10, height: 10, borderRadius: 3, background: s.color, flexShrink: 0 }} />
                      <span style={{ color: '#374151', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 150 }}>
                        {s.label}
                      </span>
                      <span style={{ color: '#9ca3af', fontWeight: 700, marginLeft: 'auto' }}>{s.value}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div style={chartHint}>Click a segment to open that client</div>
            </div>
            <div style={chartCard}>
              <div style={chartTitle}>Highest-risk clients</div>
              <BarList rows={barRows} onRowClick={(key) => openClient(key)} />
              <div style={chartHint}>Ranked by open issues · red bars carry critical findings</div>
            </div>
          </div>
        )}
      </div>
    );
  };
  const riskAccent = (c) => {
    if (c.criticalIssueCount > 0) return SEVERITY_COLORS.critical.solid;
    if (c.openIssueCount > 0) return SEVERITY_COLORS.high.solid;
    if (c.issueCount > 0) return SEVERITY_COLORS.low.solid;
    return '#22c55e';
  };
  const renderList = () => {
    if (loading) return <p style={{ color: '#6b7280' }}>Loading clients…</p>;
    if (!filtered.length) {
      return (
        <div style={emptyBox}>
          <p style={{ margin: 0, fontSize: 16 }}>
            {search ? 'No clients match that search' : 'No clients yet'}
          </p>
          <p style={{ margin: '8px 0 0 0', fontSize: 14 }}>
            Clients are created automatically on first document upload, or added here directly.
          </p>
        </div>
      );
    }
    return (
      <>
        {renderPortfolioBand()}
        <div style={cardGrid}>
          {filtered.map((c) => {
            const accent = riskAccent(c);
            const openPct = c.issueCount ? Math.round(((c.openIssueCount || 0) / c.issueCount) * 100) : 0;
            return (
              <div
                key={c._id}
                className="cp-card"
                style={{
                  ...clientCard,
                  borderTop: `4px solid ${accent}`,
                  opacity: c.status === 'archived' ? 0.65 : 1
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                  <div style={{ minWidth: 0 }}>
                    <h3
                      style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#1f2937', cursor: 'pointer' }}
                      onClick={() => openClient(c._id)}
                    >
                      {c.name}
                    </h3>
                    {c.contactName && (
                      <p style={{ margin: '4px 0 0 0', fontSize: 13, color: '#6b7280' }}>
                        {c.contactName}{c.contactEmail ? ` · ${c.contactEmail}` : ''}
                      </p>
                    )}
                  </div>
                  {c.status === 'archived' && <span style={{ ...pill, background: '#f3f4f6' }}>Archived</span>}
                </div>
                <div style={metricRow}>
                  <div style={metric}>
                    <div style={metricNum}>{c.projectCount ?? 0}</div>
                    <div style={metricLabel}>Projects</div>
                  </div>
                  <div style={metric}>
                    <div style={metricNum}>{c.docCount ?? 0}</div>
                    <div style={metricLabel}>Documents</div>
                  </div>
                  <div style={metric}>
                    <div style={{ ...metricNum, color: c.openIssueCount ? '#b91c1c' : '#065f46' }}>
                      {c.openIssueCount ?? 0}
                    </div>
                    <div style={metricLabel}>Open issues</div>
                  </div>
                </div>
                {c.issueCount > 0 && (
                  <div style={{ marginTop: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#6b7280', marginBottom: 3 }}>
                      <span>Issue resolution</span>
                      <span style={{ fontWeight: 700 }}>{c.issueCount - (c.openIssueCount || 0)}/{c.issueCount} closed</span>
                    </div>
                    <div style={{ height: 6, background: '#fee2e2', borderRadius: 999, overflow: 'hidden' }}>
                      <div
                        className="cp-bar-fill"
                        style={{ height: '100%', width: `${100 - openPct}%`, background: '#22c55e', borderRadius: 999 }}
                      />
                    </div>
                  </div>
                )}
                {c.criticalIssueCount > 0 && (
                  <div style={criticalBanner}>
                    ⚠ {c.criticalIssueCount} critical issue{c.criticalIssueCount === 1 ? '' : 's'} open
                  </div>
                )}
                <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 10 }}>
                  Last activity: {fmtDate(c.lastActivityAt)}
                </div>
                <div style={{ display: 'flex', gap: 6, marginTop: 12, flexWrap: 'wrap' }}>
                  <button onClick={() => openClient(c._id)} style={smallPrimary}>Open</button>
                  <button onClick={() => setEditing({ ...c })} style={smallSecondary}>Edit</button>
                  <button
                    onClick={() => toggleArchive(c, c.status !== 'archived')}
                    style={smallSecondary}
                  >
                    {c.status === 'archived' ? 'Restore' : 'Archive'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </>
    );
  };
  // ─────────────────────────────────────────────────────────── DETAIL VIEW
  const jumpToIssues = (opts = {}) => {
    setDetailTab('issues');
    if (opts.severity !== undefined) setIssueSeverityFilter(opts.severity);
    if (opts.status !== undefined) setIssueStatusFilter(opts.status);
    if (opts.type !== undefined) setIssueTypeFilter(opts.type);
    setExpandedIssueId(opts.expandId || null);
  };
  const renderDetail = () => {
    if (detailLoading) return <p style={{ color: '#6b7280' }}>Loading client…</p>;
    if (!detail) return <div style={emptyBox}>Client could not be loaded.</div>;
    const { client, projects = [], documents = [], summary = {} } = detail;
    // Prefer the full hydrated issue docs; fall back to the slim projection.
    const issues = fullIssues || detail.issues || [];
    const usingFullDocs = !!fullIssues;
    const projectNameFor = (pid) => projects.find((p) => String(p._id) === String(pid))?.name || '-';
    const docNameFor = (did) => {
      const d = documents.find((x) => String(x._id) === String(did));
      return d ? d.originalName || d.fileName : '-';
    };
    const isOpenIssue = (i) => OPEN_STATUSES.includes(i.status);
    const visibleIssues = issues.filter((i) => {
      if (issueStatusFilter === 'open' && !isOpenIssue(i)) return false;
      if (issueStatusFilter !== 'all' && issueStatusFilter !== 'open' && i.status !== issueStatusFilter) return false;
      if (issueSeverityFilter !== 'all' && i.severity !== issueSeverityFilter) return false;
      if (issueTypeFilter !== 'all' && i.type !== issueTypeFilter) return false;
      return true;
    });
    const openIssues = issues.filter(isOpenIssue);
    // Chart data
    const sevSegs = SEVERITY_ORDER.map((sev) => ({
      key: sev,
      label: titleCase(sev),
      value: (summary.openBySeverity && summary.openBySeverity[sev]) || 0,
      color: SEVERITY_COLORS[sev].solid
    }));
    const typeRows = Object.entries(summary.byType || {})
      .sort((a, b) => b[1] - a[1])
      .map(([type, n]) => ({
        key: type,
        label: titleCase(type),
        value: n,
        color: type === 'material_weakness' ? SEVERITY_COLORS.critical.solid
          : type === 'significant_deficiency' ? SEVERITY_COLORS.high.solid
          : '#6366f1'
      }));
    const statusSegs = Object.entries(summary.byStatus || {}).map(([st, n]) => ({
      key: st,
      label: titleCase(st),
      value: n,
      color: (STATUS_COLORS[st] || STATUS_COLORS.closed).solid
    }));
    const typeOptions = [...new Set(issues.map((i) => i.type).filter(Boolean))];
    // Issue type mix donut (all statuses) — same palette logic as the bars
    const TYPE_PALETTE = {
      material_weakness: SEVERITY_COLORS.critical.solid,
      significant_deficiency: SEVERITY_COLORS.high.solid,
      finding: '#6366f1',
      observation: '#0ea5e9',
      control_deficiency: '#8b5cf6',
      compliance_violation: '#ec4899',
      other: '#94a3b8'
    };
    const typeSegs = Object.entries(summary.byType || {})
      .sort((a, b) => b[1] - a[1])
      .map(([type, n]) => ({
        key: type,
        label: titleCase(type),
        value: n,
        color: TYPE_PALETTE[type] || '#6366f1'
      }));
    // ⭐ Remediation coverage for the Overview card. Prefers the SERVER-computed
    // summary.remediation (stages are hasResponse → hasPlan → asserted →
    // verified, matching the closure gate exactly); falls back to deriving it
    // locally if the backend predates the remediation build.
    const srv = remediationSummary;
    const localRem = usingFullDocs && !srv
      ? openIssues.reduce(
          (acc, i) => {
            const r = !!(i.managementResponse && i.managementResponse.text);
            const p = !!(i.remediationPlan && i.remediationPlan.text);
            if (r && p) acc.staged += 1;
            else if (p) acc.planOnly += 1;
            else if (r) acc.responseOnly += 1;
            else acc.unaddressed += 1;
            if (i.owner && (i.owner.name || i.owner.adminId)) acc.withOwner += 1;
            if (i.remediationPlan && i.remediationPlan.targetDate) acc.withTargetDate += 1;
            const due = i.dueDate || (i.remediationPlan && i.remediationPlan.targetDate);
            if (due && new Date(due) < new Date() && !(i.remediationPlan && i.remediationPlan.completedAt)) acc.overdue += 1;
            return acc;
          },
          { staged: 0, planOnly: 0, responseOnly: 0, unaddressed: 0, withOwner: 0, withTargetDate: 0, overdue: 0 }
        )
      : null;
    const remStats = srv || localRem;
    const remSegs = remStats
      ? [
          { key: 'staged', label: 'Response + plan', value: remStats.staged || 0, color: '#22c55e' },
          { key: 'planOnly', label: 'Plan only', value: remStats.planOnly || 0, color: '#0ea5e9' },
          { key: 'responseOnly', label: 'Response only', value: remStats.responseOnly || 0, color: '#f59e0b' },
          { key: 'unaddressed', label: 'Unaddressed', value: remStats.unaddressed || 0, color: '#ef4444' }
        ]
      : [];
    const coverageRows = remStats
      ? [
          { key: 'owned', label: 'Owner assigned', value: remStats.withOwner || 0, sub: `of ${openIssues.length}`, color: '#8b5cf6' },
          { key: 'targeted', label: 'Target date set', value: remStats.withTargetDate || 0, sub: `of ${openIssues.length}`, color: '#0ea5e9' },
          { key: 'verified', label: 'Verified', value: remStats.verified || 0, sub: `of ${openIssues.length}`, color: '#22c55e' },
          { key: 'overdue', label: 'Overdue', value: remStats.overdue || 0, sub: `of ${openIssues.length}`, color: '#ef4444' }
        ]
      : [];
    const coveragePct = remStats
      ? (remStats.coveragePct != null
          ? remStats.coveragePct
          : (openIssues.length ? Math.round(((remStats.staged || 0) / openIssues.length) * 100) : 0))
      : 0;
    return (
      <>
        {detail.redirected && (
          <div style={noticeBox}>
            This client was merged into another record. Showing the surviving client.
          </div>
        )}
        <div style={detailHeaderCard}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24 }}>
            <div>
              <div style={metricLabel}>Contact</div>
              <div style={{ fontWeight: 600 }}>{client.contactName || '-'}</div>
              <div style={{ fontSize: 13, color: '#6b7280' }}>
                {client.contactEmail || ''}{client.contactPhone ? ` · ${client.contactPhone}` : ''}
              </div>
            </div>
            <div>
              <div style={metricLabel}>Projects</div>
              <div style={metricNum}>{summary.projectCount ?? projects.length}</div>
            </div>
            <div>
              <div style={metricLabel}>Documents</div>
              <div style={metricNum}>{summary.docCount ?? documents.length}</div>
            </div>
            <div>
              <div style={metricLabel}>Open issues</div>
              <div style={{ ...metricNum, color: summary.openIssueCount ? '#b91c1c' : '#065f46' }}>
                {summary.openIssueCount ?? 0}
              </div>
            </div>
            {remStats && (remStats.overdue || 0) > 0 && (
              <div>
                <div style={metricLabel}>Overdue</div>
                <div style={{ ...metricNum, color: '#b91c1c' }}>{remStats.overdue}</div>
              </div>
            )}
            {fmtMoney(summary.openExposureAmount) && summary.openExposureAmount > 0 && (
              <div>
                <div style={metricLabel}>Open exposure</div>
                <div style={{ ...metricNum, color: '#b91c1c' }}>{fmtMoney(summary.openExposureAmount)}</div>
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
            <button onClick={() => setEditing({ ...client })} style={smallSecondary}>Edit client</button>
            <button
              onClick={() => toggleArchive(client, client.status !== 'archived')}
              style={smallSecondary}
            >
              {client.status === 'archived' ? 'Restore' : 'Archive'}
            </button>
          </div>
        </div>
        <div style={tabRow}>
          {[
            ['overview', 'Overview'],
            ['remediation', `Remediation (${openIssues.length})`],
            ['projects', `Projects (${projects.length})`],
            ['documents', `Documents (${documents.length})`],
            ['issues', `Issues (${issues.length})`]
          ].map(([key, label]) => (
            <button
              key={key}
              onClick={() => setDetailTab(key)}
              style={detailTab === key ? tabActive : tab}
            >
              {label}
            </button>
          ))}
        </div>
        {detailTab === 'overview' && (
          issues.length ? (
            <div style={chartsRow}>
              <div style={chartCard}>
                <div style={chartTitle}>Open findings by severity</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                  <Donut
                    segments={sevSegs}
                    centerTop={openIssues.length}
                    centerBottom="Open"
                    activeKey={issueSeverityFilter !== 'all' ? issueSeverityFilter : null}
                    onSegmentClick={(sev) => jumpToIssues({ severity: sev, status: 'open' })}
                  />
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {sevSegs.map((s) => (
                      <div
                        key={s.key}
                        onClick={() => jumpToIssues({ severity: s.key, status: 'open' })}
                        style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12, cursor: 'pointer' }}
                      >
                        <span style={{ width: 10, height: 10, borderRadius: 3, background: s.color }} />
                        <span style={{ color: '#374151', fontWeight: 600 }}>{s.label}</span>
                        <span style={{ color: '#9ca3af', fontWeight: 700 }}>{s.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div style={chartHint}>Click a severity to open the filtered issue list</div>
              </div>
              {typeSegs.length > 0 && (
                <div style={chartCard}>
                  <div style={chartTitle}>Issue type mix</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                    <Donut
                      segments={typeSegs}
                      centerTop={issues.length}
                      centerBottom="Total"
                      activeKey={issueTypeFilter !== 'all' ? issueTypeFilter : null}
                      onSegmentClick={(type) => jumpToIssues({ type, severity: 'all', status: 'all' })}
                    />
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 150 }}>
                      {typeSegs.map((s) => (
                        <div
                          key={s.key}
                          onClick={() => jumpToIssues({ type: s.key, severity: 'all', status: 'all' })}
                          style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12, cursor: 'pointer' }}
                        >
                          <span style={{ width: 10, height: 10, borderRadius: 3, background: s.color, flexShrink: 0 }} />
                          <span style={{ color: '#374151', fontWeight: 600 }}>{s.label}</span>
                          <span style={{ color: '#9ca3af', fontWeight: 700, marginLeft: 'auto' }}>{s.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div style={chartHint}>Click a type to open the filtered issue list</div>
                </div>
              )}
              {remStats && openIssues.length > 0 && (
                <div style={chartCard}>
                  <div style={chartTitle}>Remediation coverage (open issues)</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                    <Donut
                      segments={remSegs}
                      centerTop={`${coveragePct}%`}
                      centerBottom="Fully staged"
                      onSegmentClick={() => setDetailTab('remediation')}
                    />
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 150 }}>
                      {remSegs.map((s) => (
                        <div
                          key={s.key}
                          onClick={() => setDetailTab('remediation')}
                          style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12, cursor: 'pointer' }}
                        >
                          <span style={{ width: 10, height: 10, borderRadius: 3, background: s.color, flexShrink: 0 }} />
                          <span style={{ color: '#374151', fontWeight: 600 }}>{s.label}</span>
                          <span style={{ color: '#9ca3af', fontWeight: 700, marginLeft: 'auto' }}>{s.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div style={{ marginTop: 12 }}>
                    <BarList rows={coverageRows} maxRows={4} />
                  </div>
                  <div style={chartHint}>Click through to the Remediation tracker</div>
                </div>
              )}
              {typeRows.length > 0 && (
                <div style={chartCard}>
                  <div style={chartTitle}>Findings by type (ranked)</div>
                  <BarList rows={typeRows} onRowClick={(type) => jumpToIssues({ type, severity: 'all', status: 'all' })} />
                </div>
              )}
              {statusSegs.length > 0 && (
                <div style={chartCard}>
                  <div style={chartTitle}>Workflow status mix</div>
                  <StackStrip segments={statusSegs} height={14} />
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 10 }}>
                    {statusSegs.map((s) => (
                      <div
                        key={s.key}
                        onClick={() => jumpToIssues({ status: s.key, severity: 'all', type: 'all' })}
                        style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer' }}
                      >
                        <span style={{ width: 9, height: 9, borderRadius: 3, background: s.color }} />
                        <span style={{ color: '#374151', fontWeight: 600 }}>{s.label}</span>
                        <span style={{ color: '#9ca3af', fontWeight: 700 }}>{s.value}</span>
                      </div>
                    ))}
                  </div>
                  {fmtMoney(summary.openExposureAmount) && summary.openExposureAmount > 0 && (
                    <div style={{ marginTop: 14, padding: '10px 12px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8 }}>
                      <div style={{ fontSize: 11, color: '#991b1b', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 700 }}>
                        Open dollar exposure
                      </div>
                      <div style={{ fontSize: 22, fontWeight: 800, color: '#991b1b' }}>
                        {fmtMoney(summary.openExposureAmount)}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div style={emptyBox}>No findings recorded for this client yet — a clean slate.</div>
          )
        )}
        {/* ⭐ REMEDIATION TAB — working tracker. RemediationPanel owns the
            corrective actions, assertion, verification, and gated closure. */}
        {detailTab === 'remediation' && (
          !usingFullDocs ? (
            <div style={emptyBox}>
              {fullIssuesLoading ? 'Loading full issue records…' : 'Full issue records unavailable — remediation tracking needs them.'}
            </div>
          ) : (
            <RemediationPanel
              issues={issues}
              remediation={remediationSummary}
              onChanged={refreshAfterRemediation}
            />
          )
        )}
        {detailTab === 'projects' && (
          projects.length ? (
            <div style={cardGrid}>
              {projects.map((p) => (
                <div key={p._id} className="cp-card" style={{ ...clientCard, borderTop: `4px solid ${p.openIssueCount ? SEVERITY_COLORS.high.solid : '#22c55e'}` }}>
                  <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#1f2937' }}>{p.name}</h3>
                  <p style={{ margin: '4px 0 0 0', fontSize: 12, color: '#6b7280' }}>
                    Created {fmtDate(p.createdAt)}
                    {p.isCollaborative ? ' · Shared' : ''}
                  </p>
                  <div style={{ display: 'flex', gap: 8, marginTop: 10, fontSize: 13 }}>
                    <span>{p.docCount} docs</span>
                    <span style={{ color: p.openIssueCount ? '#b91c1c' : '#065f46' }}>
                      {p.openIssueCount} open
                    </span>
                  </div>
                  {onOpenProject && (
                    <button onClick={() => onOpenProject(p)} style={{ ...smallPrimary, marginTop: 10 }}>
                      Open project
                    </button>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div style={emptyBox}>No projects for this client yet.</div>
          )
        )}
        {detailTab === 'documents' && (
          documents.length ? (
            <div style={tableWrap}>
              <table style={table}>
                <thead>
                  <tr>
                    <th style={th}>Document</th>
                    <th style={th}>Project</th>
                    <th style={th}>Kind</th>
                    <th style={th}>Workflow</th>
                    <th style={th}>Uploaded</th>
                    <th style={th}></th>
                  </tr>
                </thead>
                <tbody>
                  {documents.map((d) => (
                    <tr key={d._id}>
                      <td style={td}>{d.originalName || d.fileName}</td>
                      <td style={td}>{d.projectName || projectNameFor(d.projectId)}</td>
                      <td style={td}>{titleCase(d.kind)}</td>
                      <td style={td}>{d.workflowAssignment?.templateName || '-'}</td>
                      <td style={td}>{fmtDate(d.createdAt)}</td>
                      <td style={td}>
                        {onOpenDocument && (
                          <button onClick={() => onOpenDocument(d)} style={smallSecondary}>Open</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={emptyBox}>No documents for this client yet.</div>
          )
        )}
        {detailTab === 'issues' && (
          <>
            {/* Filter bar: severity chips + status + type */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', gap: 6 }}>
                <button
                  className="cp-chip"
                  onClick={() => setIssueSeverityFilter('all')}
                  style={issueSeverityFilter === 'all' ? chipActive : chip}
                >
                  All severities
                </button>
                {SEVERITY_ORDER.map((sev) => {
                  const col = SEVERITY_COLORS[sev];
                  const active = issueSeverityFilter === sev;
                  return (
                    <button
                      key={sev}
                      className="cp-chip"
                      onClick={() => setIssueSeverityFilter(active ? 'all' : sev)}
                      style={{
                        ...chip,
                        background: active ? col.solid : col.bg,
                        borderColor: col.border,
                        color: active ? '#fff' : col.text
                      }}
                    >
                      {titleCase(sev)}
                    </button>
                  );
                })}
              </div>
              <select
                value={issueStatusFilter}
                onChange={(e) => setIssueStatusFilter(e.target.value)}
                style={input}
              >
                <option value="all">All statuses</option>
                <option value="open">Open (any stage)</option>
                <option value="in_remediation">In remediation</option>
                <option value="pending_review">Pending review</option>
                <option value="escalated">Escalated</option>
                <option value="closed">Closed</option>
                <option value="voided">Voided</option>
              </select>
              {typeOptions.length > 1 && (
                <select
                  value={issueTypeFilter}
                  onChange={(e) => setIssueTypeFilter(e.target.value)}
                  style={input}
                >
                  <option value="all">All types</option>
                  {typeOptions.map((t) => (
                    <option key={t} value={t}>{titleCase(t)}</option>
                  ))}
                </select>
              )}
              <span style={{ fontSize: 13, color: '#6b7280' }}>
                {visibleIssues.length} shown{fullIssuesLoading ? ' · loading full records…' : ''}
              </span>
            </div>
            {visibleIssues.length ? (
              <div style={tableWrap}>
                <table style={table}>
                  <thead>
                    <tr>
                      <th style={th}></th>
                      <th style={th}>#</th>
                      <th style={th}>Issue</th>
                      <th style={th}>Project</th>
                      <th style={th}>Document</th>
                      <th style={th}>Type</th>
                      <th style={th}>Severity</th>
                      <th style={th}>Status</th>
                      <th style={th}>Amount</th>
                      <th style={th}>WP</th>
                      <th style={th}>Raised</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleIssues.map((i) => {
                      const sev = SEVERITY_COLORS[i.severity] || SEVERITY_COLORS.low;
                      const st = STATUS_COLORS[i.status] || STATUS_COLORS.closed;
                      const expanded = expandedIssueId === String(i._id);
                      const commentCount = (i.comments || []).filter((c) => !c.deletedAt).length;
                      return (
                        <React.Fragment key={i._id}>
                          <tr
                            className="cp-row"
                            onClick={() => setExpandedIssueId(expanded ? null : String(i._id))}
                            style={{
                              background: expanded ? '#f9fafb' : undefined,
                              borderLeft: `3px solid ${sev.solid}`
                            }}
                          >
                            <td style={{ ...td, width: 24, color: '#9ca3af', fontWeight: 700 }}>
                              {expanded ? '▾' : '▸'}
                            </td>
                            <td style={td}>{i.issueNumber ?? '-'}</td>
                            <td style={{ ...td, maxWidth: 320, fontWeight: 600 }}>
                              {i.title}
                              {i.isOverdue && (
                                <span style={{ ...pill, marginLeft: 6, background: '#fef2f2', borderColor: '#ef4444', color: '#991b1b' }}>
                                  ⏰ Overdue
                                </span>
                              )}
                              {commentCount > 0 && (
                                <span style={{ ...pill, marginLeft: 6, background: '#eef2ff', borderColor: '#c7d2fe', color: '#3730a3' }}>
                                  💬 {commentCount}
                                </span>
                              )}
                            </td>
                            <td style={td}>{i.projectName || projectNameFor(i.projectId)}</td>
                            <td style={td}>{i.documentName || docNameFor(i.docId)}</td>
                            <td style={td}>{titleCase(i.type)}</td>
                            <td style={td}>
                              <span style={{ ...pill, background: sev.bg, borderColor: sev.border, color: sev.text }}>
                                {titleCase(i.severity)}
                              </span>
                            </td>
                            <td style={td}>
                              <span style={{ ...pill, background: st.bg, borderColor: 'transparent', color: st.text }}>
                                {titleCase(i.status)}
                              </span>
                            </td>
                            <td style={td}>{fmtMoney(i.amount) || '-'}</td>
                            <td style={td}>{i.workpaperRef || '-'}</td>
                            <td style={td}>{fmtDate(i.raisedAt || i.createdAt)}</td>
                          </tr>
                          {expanded && (
                            <tr>
                              <td colSpan={11} style={{ ...td, background: '#fafafa', padding: 0 }}>
                                <div style={expandBox}>
                                  {usingFullDocs ? (
                                    <>
                                      {i.description && (
                                        <div style={expandBlock}>
                                          <div style={expandLabel}>Description</div>
                                          <div style={expandText}>{i.description}</div>
                                        </div>
                                      )}
                                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 14 }}>
                                        <div style={expandBlock}>
                                          <div style={expandLabel}>Management response</div>
                                          {i.managementResponse?.text ? (
                                            <>
                                              <div style={expandText}>{i.managementResponse.text}</div>
                                              {i.managementResponse.respondedAt && (
                                                <div style={expandMeta}>
                                                  {i.managementResponse.respondedBy?.name || 'Management'} · {fmtDate(i.managementResponse.respondedAt)}
                                                </div>
                                              )}
                                            </>
                                          ) : (
                                            <div style={{ ...expandText, color: '#9ca3af', fontStyle: 'italic' }}>Awaiting response</div>
                                          )}
                                        </div>
                                        <div style={expandBlock}>
                                          <div style={expandLabel}>Remediation plan</div>
                                          {i.remediationPlan?.text ? (
                                            <>
                                              <div style={expandText}>{i.remediationPlan.text}</div>
                                              <div style={expandMeta}>
                                                {i.remediationPlan.targetDate ? `Target: ${fmtDate(i.remediationPlan.targetDate)}` : ''}
                                                {i.remediationPlan.assertedAt ? ` · Asserted ${fmtDate(i.remediationPlan.assertedAt)}` : ''}
                                              </div>
                                            </>
                                          ) : (
                                            <div style={{ ...expandText, color: '#9ca3af', fontStyle: 'italic' }}>No plan documented</div>
                                          )}
                                        </div>
                                      </div>
                                      {/* ⭐ Corrective actions */}
                                      {(i.remediationActions || []).length > 0 && (
                                        <div style={expandBlock}>
                                          <div style={expandLabel}>Corrective actions</div>
                                          {i.remediationActions.map((a) => (
                                            <div key={a._id} style={{ ...expandText, display: 'flex', gap: 8, alignItems: 'baseline' }}>
                                              <span style={{ ...pill, background: a.status === 'completed' ? '#f0fdf4' : '#f3f4f6', color: a.status === 'completed' ? '#065f46' : '#4b5563', borderColor: 'transparent' }}>
                                                {titleCase(a.status)}
                                              </span>
                                              <span>
                                                {a.description}
                                                {a.targetDate ? <span style={expandMeta}> · due {fmtDate(a.targetDate)}</span> : null}
                                              </span>
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                      {/* ⭐ Independent verification */}
                                      {i.verification && i.verification.status && i.verification.status !== 'not_started' && (
                                        <div style={expandBlock}>
                                          <div style={expandLabel}>Independent verification</div>
                                          {i.verification.status === 'verified' ? (
                                            <>
                                              <div style={{ ...expandText, color: '#065f46' }}>
                                                ✓ Verified {fmtDate(i.verification.verifiedAt)}
                                                {i.verification.verifiedBy?.name ? ` by ${i.verification.verifiedBy.name}` : ''}
                                              </div>
                                              {i.verification.proceduresPerformed && (
                                                <div style={expandText}>{i.verification.proceduresPerformed}</div>
                                              )}
                                              {i.verification.evidenceRef && (
                                                <div style={expandMeta}>Evidence: {i.verification.evidenceRef}</div>
                                              )}
                                            </>
                                          ) : i.verification.status === 'rejected' ? (
                                            <div style={{ ...expandText, color: '#9a3412' }}>
                                              Assertion rejected{i.verification.rejectionCount ? ` (×${i.verification.rejectionCount})` : ''}: {i.verification.rejectionReason}
                                            </div>
                                          ) : (
                                            <div style={{ ...expandText, color: '#1e40af' }}>Awaiting independent verification</div>
                                          )}
                                        </div>
                                      )}
                                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 18, marginTop: 4 }}>
                                        <div>
                                          <span style={expandLabel}>Raised by </span>
                                          <span style={{ fontSize: 13, fontWeight: 600 }}>{i.raisedBy?.name || i.raisedBy?.email || '-'}</span>
                                        </div>
                                        {i.owner?.name && (
                                          <div>
                                            <span style={expandLabel}>Owner </span>
                                            <span style={{ fontSize: 13, fontWeight: 600 }}>{i.owner.name}</span>
                                          </div>
                                        )}
                                        {i.dueDate && (
                                          <div>
                                            <span style={expandLabel}>Due </span>
                                            <span style={{ fontSize: 13, fontWeight: 600, color: new Date(i.dueDate) < new Date() && isOpenIssue(i) ? '#b91c1c' : '#111827' }}>
                                              {fmtDate(i.dueDate)}
                                            </span>
                                          </div>
                                        )}
                                        {i.closedAt && (
                                          <div>
                                            <span style={expandLabel}>Closed </span>
                                            <span style={{ fontSize: 13, fontWeight: 600 }}>
                                              {fmtDate(i.closedAt)}{i.closedBy?.name ? ` by ${i.closedBy.name}` : ''}
                                            </span>
                                          </div>
                                        )}
                                        {i.closedWithoutVerification && (
                                          <span style={{ ...pill, background: '#fff7ed', borderColor: '#f97316', color: '#9a3412' }}>
                                            Closed without verification
                                          </span>
                                        )}
                                        {(i.tags || []).length > 0 && (
                                          <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
                                            {i.tags.map((t) => (
                                              <span key={t} style={{ ...pill, background: '#f3f4f6', color: '#374151' }}>{t}</span>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                      {i.closureNotes && (
                                        <div style={expandBlock}>
                                          <div style={expandLabel}>Closure notes</div>
                                          <div style={expandText}>{i.closureNotes}</div>
                                        </div>
                                      )}
                                    </>
                                  ) : (
                                    <div style={{ ...expandText, color: '#6b7280' }}>
                                      {fullIssuesLoading
                                        ? 'Loading full issue record…'
                                        : 'Full issue details unavailable — showing summary fields only.'}
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div style={emptyBox}>No issues match this filter.</div>
            )}
          </>
        )}
      </>
    );
  };
  // ─────────────────────────────────────────────────────────── DUPES VIEW
  const renderDupes = () => {
    if (dupesLoading) return <p style={{ color: '#6b7280' }}>Scanning…</p>;
    if (!dupes.length) {
      return <div style={emptyBox}>No near-duplicate client names found.</div>;
    }
    return (
      <>
        <p style={{ color: '#6b7280', fontSize: 14, marginTop: 0 }}>
          Names are grouped after stripping punctuation and entity suffixes (Inc., LLC, Corp.).
          The record with the most attached work is suggested as the survivor.
        </p>
        {dupes.map((g) => {
          const survivor = g.members.find((m) => String(m._id) === String(g.suggestedSurvivorId)) || g.members[0];
          return (
            <div key={g.nameKey} style={{ ...formCard, marginBottom: 12 }}>
              <div style={{ fontWeight: 700, marginBottom: 10 }}>{g.members.length} records match “{g.nameKey}”</div>
              <div style={tableWrap}>
                <table style={table}>
                  <thead>
                    <tr>
                      <th style={th}>Name</th>
                      <th style={th}>Projects</th>
                      <th style={th}>Docs</th>
                      <th style={th}>Created</th>
                      <th style={th}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {g.members.map((m) => {
                      const isSurvivor = String(m._id) === String(survivor._id);
                      return (
                        <tr key={m._id}>
                          <td style={td}>
                            {m.name}{' '}
                            {isSurvivor && (
                              <span style={{ ...pill, background: '#f0fdf4', borderColor: '#22c55e', color: '#065f46' }}>
                                keep
                              </span>
                            )}
                          </td>
                          <td style={td}>{m.projectCount}</td>
                          <td style={td}>{m.docCount}</td>
                          <td style={td}>{fmtDate(m.createdAt)}</td>
                          <td style={td}>
                            {!isSurvivor && (
                              <button
                                onClick={() => mergeInto(survivor._id, m._id, m.name, survivor.name)}
                                disabled={merging === m._id}
                                style={smallPrimary}
                              >
                                {merging === m._id ? 'Merging…' : `Merge into ${survivor.name}`}
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}
      </>
    );
  };
  return (
    <section style={sectionStyle}>
      <style>{`
        .cp-card { transition: transform .15s ease, box-shadow .15s ease; }
        .cp-card:hover { transform: translateY(-2px); box-shadow: 0 10px 24px rgba(17,24,39,.10); }
        .cp-row { cursor: pointer; }
        .cp-row:hover { background: #f9fafb; }
        .cp-chip { transition: filter .12s ease, transform .12s ease; }
        .cp-chip:hover { filter: brightness(.96); }
        .cp-chip:active { transform: scale(.97); }
        .cp-bar-fill { transition: width .6s cubic-bezier(.4,0,.2,1); }
        .cp-barrow:hover { opacity: .85; }
      `}</style>
      {renderHeader()}
      {error && <div style={errorBox}>{error}</div>}
      {editing && renderEditForm()}
      {view === 'list' && showNewClient && renderNewClientForm()}
      {view === 'list' && renderList()}
      {view === 'detail' && renderDetail()}
      {view === 'dupes' && renderDupes()}
    </section>
  );
};
// ------------------------------------------------------------------- styles
const sectionStyle = { marginBottom: 32 };
const headerRow = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: 12,
  gap: 12,
  flexWrap: 'wrap'
};
const h2 = { margin: 0, fontSize: 20, fontWeight: 800, color: '#111827' };
const backButton = {
  background: '#f3f4f6',
  border: '1px solid #e5e7eb',
  borderRadius: 6,
  padding: '6px 10px',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
  color: '#374151'
};
const input = {
  padding: '8px 10px',
  border: '1px solid #d1d5db',
  borderRadius: 6,
  fontSize: 14,
  outline: 'none',
  background: '#fff'
};
const primaryButton = {
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: '#fff',
  border: 'none',
  padding: '8px 16px',
  borderRadius: 6,
  fontSize: 14,
  fontWeight: 600,
  cursor: 'pointer'
};
const cancelButton = { ...primaryButton, background: '#dc2626' };
const secondaryButton = {
  background: '#fff',
  color: '#374151',
  border: '1px solid #d1d5db',
  padding: '8px 14px',
  borderRadius: 6,
  fontSize: 14,
  fontWeight: 600,
  cursor: 'pointer'
};
const smallPrimary = { ...primaryButton, padding: '5px 10px', fontSize: 12 };
const smallSecondary = { ...secondaryButton, padding: '5px 10px', fontSize: 12 };
const formCard = {
  background: '#fff',
  border: '1px solid #e5e7eb',
  borderRadius: 12,
  padding: 16,
  marginBottom: 16
};
const detailHeaderCard = { ...formCard, background: '#fafafa' };
const cardGrid = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
  gap: 16
};
const clientCard = {
  background: '#fff',
  border: '1px solid #e5e7eb',
  borderRadius: 12,
  padding: 16
};
const metricRow = { display: 'flex', gap: 16, marginTop: 14 };
const metric = { flex: 1 };
const metricNum = { fontSize: 20, fontWeight: 800, color: '#111827', lineHeight: 1.1 };
const metricLabel = { fontSize: 11, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4, marginTop: 2 };
const criticalBanner = {
  marginTop: 10,
  padding: '6px 10px',
  background: '#fef2f2',
  border: '1px solid #fecaca',
  color: '#991b1b',
  borderRadius: 6,
  fontSize: 12,
  fontWeight: 600
};
const pill = {
  display: 'inline-block',
  padding: '2px 8px',
  borderRadius: 999,
  border: '1px solid #e5e7eb',
  fontSize: 11,
  fontWeight: 700
};
const chip = {
  ...pill,
  cursor: 'pointer',
  background: '#fff',
  color: '#4b5563',
  padding: '5px 12px',
  fontSize: 12
};
const chipActive = { ...chip, background: '#111827', color: '#fff', borderColor: '#111827' };
const tabRow = { display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap' };
const tab = {
  background: '#fff',
  border: '1px solid #e5e7eb',
  borderRadius: 8,
  padding: '7px 14px',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
  color: '#4b5563'
};
const tabActive = { ...tab, background: '#111827', color: '#fff', borderColor: '#111827' };
const tableWrap = {
  overflowX: 'auto',
  border: '1px solid #e5e7eb',
  borderRadius: 10,
  background: '#fff'
};
const table = { width: '100%', borderCollapse: 'collapse', fontSize: 13 };
const th = {
  textAlign: 'left',
  padding: '10px 12px',
  background: '#f9fafb',
  borderBottom: '1px solid #e5e7eb',
  fontWeight: 700,
  color: '#374151',
  whiteSpace: 'nowrap'
};
const td = {
  padding: '10px 12px',
  borderBottom: '1px solid #f3f4f6',
  color: '#1f2937',
  verticalAlign: 'top'
};
const emptyBox = {
  padding: 24,
  textAlign: 'center',
  color: '#6b7280',
  background: '#fff',
  border: '1px dashed #e5e7eb',
  borderRadius: 12
};
const errorBox = {
  background: '#fef2f2',
  border: '1px solid #fecaca',
  color: '#991b1b',
  padding: 12,
  borderRadius: 8,
  marginBottom: 16,
  fontSize: 14
};
const noticeBox = {
  background: '#eff6ff',
  border: '1px solid #bfdbfe',
  color: '#1e40af',
  padding: 12,
  borderRadius: 8,
  marginBottom: 16,
  fontSize: 14
};
// ---- analytics band styles ----
const analyticsBand = { marginBottom: 20 };
const kpiRow = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
  gap: 12,
  marginBottom: 16
};
const kpiCard = {
  background: '#fff',
  border: '1px solid #e5e7eb',
  borderTop: '4px solid #6366f1',
  borderRadius: 12,
  padding: '14px 16px'
};
const kpiNum = { fontSize: 26, fontWeight: 800, color: '#111827', lineHeight: 1.1 };
const kpiLabel = { fontSize: 11, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 4, fontWeight: 700 };
const chartsRow = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
  gap: 16,
  marginBottom: 20
};
const chartCard = {
  background: '#fff',
  border: '1px solid #e5e7eb',
  borderRadius: 12,
  padding: 16
};
const chartTitle = { fontSize: 13, fontWeight: 800, color: '#111827', marginBottom: 12, textTransform: 'uppercase', letterSpacing: 0.4 };
const chartHint = { fontSize: 11, color: '#9ca3af', marginTop: 10 };
// ---- expandable issue row styles ----
const expandBox = {
  padding: '14px 18px 16px 42px',
  display: 'flex',
  flexDirection: 'column',
  gap: 12
};
const expandBlock = { display: 'flex', flexDirection: 'column', gap: 4 };
const expandLabel = { fontSize: 11, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 700 };
const expandText = { fontSize: 13, color: '#1f2937', whiteSpace: 'pre-wrap', lineHeight: 1.5 };
const expandMeta = { fontSize: 12, color: '#9ca3af' };
export default ClientsPanel;